import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useHistory } from 'react-router'
import { useColumnOrder, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from 'react-table'
import RemoveCircleOutlineOutlinedIcon from '@material-ui/icons/RemoveCircleOutlineOutlined'
import AddIcon from '@material-ui/icons/Add'

import { useRemoteCollection } from 'civic-champs-shared/api/hooks'
import { ContainedButton } from 'civic-champs-shared/core'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import { TableMenuButton } from 'civic-champs-shared/core/TableMenuButton'
import { PersonRef } from 'civic-champs-shared/common/types'

import Loading from 'components/Loading'
import { ExtendedPagedTable } from 'core/table/components/ExtendedPagedTable'
import DEFAULT_FILTERS from 'core/table/filters'
import { useConditionalSelectColumn } from 'core/table/table-hooks/useSelectColumn'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import { useFeatureEnabled } from 'core/feature/hooks'
import { Activity } from 'champion/utils/interface'
import useActivityLogColumns, { useStyles as useColumnStyles } from '../hooks/useActivityLogColumns'
import ExportActivityLogButton from './ExportActivityLogButton'
import { AddActivityDialog, AddOrEditActivityPrompt } from './index'
import { useEditActivityPrompt, useRemoveActivitiesPrompt } from '../hooks'
import CUSTOM_FILTERS from '../utils/filters'

const tableName = 'profile-activities'

export const ProfileActivitiesTable = ({
  activities: profileActivities,
  person,
  reloadProfile,
}: {
  activities: Activity[]
  person: PersonRef
  reloadProfile: () => void
}) => {
  const history = useHistory()
  const columnStyles = useColumnStyles()
  const skipReset = useRef<boolean>()
  const isBulkActivityEditEnabled = useFeatureEnabled('BulkActivityEdit')
  const showAddPrompt = useShowPrompt(isBulkActivityEditEnabled ? AddOrEditActivityPrompt : AddActivityDialog)

  const [activities, operations, eventListeners] = useRemoteCollection<Activity>()
  const { syncCollection, eagerAdd, eagerReplace, eagerRemoveMany } = operations

  useEffect(() => {
    syncCollection(profileActivities)
  }, [profileActivities, syncCollection])

  const editActivity = useEditActivityPrompt(eagerReplace)
  const removeActivities = useRemoveActivitiesPrompt(eagerRemoveMany)

  const onEdit = useCallback(
    (activity: { id: number }) => {
      editActivity(activity as Activity).then(() => reloadProfile())
    },
    [editActivity, reloadProfile],
  )

  const columns = useActivityLogColumns({ onEdit }, undefined, { forProfile: true })
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()

  useEffect(() => {
    fetchColumnState(tableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const table = useTable(
    {
      initialState: {
        hiddenColumns: ['checkOut', 'reflections', 'groups', 'volunteerTags'],
        // @ts-ignore
        globalFilter: '',
      },
      // @ts-ignore
      filterTypes: { ...DEFAULT_FILTERS, ...CUSTOM_FILTERS },
      data: activities,
      columns,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
    },
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, columnStyles.selectColumn),
  )

  const [selectedRows, selectedRowsCount] = useMemo(
    () => [
      table.selectedFlatRows.map(({ original }: { original: Activity }) => original),
      table.selectedFlatRows.length,
    ],
    [table.selectedFlatRows],
  )

  const handleAddActivity = useCallback(async () => {
    const savedActivity = await showAddPrompt(isBulkActivityEditEnabled ? { initialVolunteers: [person] } : {})
    if (savedActivity) {
      eagerAdd(savedActivity)
      reloadProfile()
    }
  }, [eagerAdd, isBulkActivityEditEnabled, person, reloadProfile, showAddPrompt])

  const onRemoveActivity = useCallback(async () => {
    await removeActivities(selectedRows)
    table.toggleAllRowsSelected(false)
    reloadProfile()
  }, [removeActivities, reloadProfile, selectedRows, table])

  if (columnStateLoading) {
    return <Loading />
  }

  return (
    <ExtendedPagedTable<Activity>
      history={history}
      table={table}
      columns={columns}
      columnState={columnState}
      tableName={tableName}
      searchPlaceholder="Search Activities"
      skipReset={skipReset}
      eventListeners={eventListeners}
      buttons={
        <>
          <ExportActivityLogButton data={selectedRows} disabled={!selectedRowsCount} />
          <TableMenuButton
            startIcon={<RemoveCircleOutlineOutlinedIcon />}
            disabled={!selectedRowsCount}
            onClick={onRemoveActivity}
          >
            Remove
          </TableMenuButton>
        </>
      }
      addButton={
        <ContainedButton startIcon={<AddIcon />} onClick={handleAddActivity}>
          Add Activity
        </ContainedButton>
      }
    />
  )
}
